import type { UseQueryResult } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"

import { queryKeys } from "~/api/cache"
import { redirectToPowurProjects } from "~/containers/BatteryOnlyProposal/redirectToPowurProjects"
import EnvVars from "~/env-vars"

import type { BatteryProposalDto } from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

const ACCESS_FAILURE_STATUSES = new Set([401, 403, 404])

const getResponseStatus = (error: unknown): number | undefined => {
  if (axios.isAxiosError(error)) {
    return error.response?.status
  }

  return undefined
}

export const isBatteryProposalAccessFailure = (error: unknown): boolean =>
  ACCESS_FAILURE_STATUSES.has(getResponseStatus(error) ?? 0)

/** An access failure is an answer; anything else retries, thrice. */
export const retryBatteryProposalRead = (
  failureCount: number,
  error: unknown,
): boolean => !isBatteryProposalAccessFailure(error) && failureCount < 3

export default function useBatteryProposal(
  proposalId: string | undefined,
): UseQueryResult<BatteryProposalDto, unknown> {
  return useQuery({
    queryKey: queryKeys.batteryProposal.byId(proposalId ?? ``),
    queryFn: () => batteryProposalService.getById(proposalId ?? ``),
    enabled: Boolean(proposalId),
    retry: retryBatteryProposalRead,
    onError: (error) => {
      const status = getResponseStatus(error)
      if (!ACCESS_FAILURE_STATUSES.has(status ?? 0)) {
        return
      }

      if (status === 401) {
        localStorage.setItem(`jwt`, ``)
        const { pathname, search, hash } = window.location
        sessionStorage.setItem(
          `redirectUrl`,
          JSON.stringify(`${pathname}${search}${hash}`),
        )
        window.location.replace(`${EnvVars.API_URL}/auth/sso/login`)
        return
      }

      redirectToPowurProjects()
    },
  })
}
